import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo, TripleSlash } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/constants";

const DEMO = [
  { label: "Admin", email: "admin@lassocommunities.com", password: "LassoAdmin2026!" },
  { label: "Regional Mgr", email: "rm.southeast@lassocommunities.com", password: "LassoDemo2026!" },
  { label: "Community Mgr", email: "cm.forestacres@lassocommunities.com", password: "LassoDemo2026!" },
  { label: "Maintenance", email: "tech.forestacres@lassocommunities.com", password: "LassoDemo2026!" },
];

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.7z" />
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9h-4v3.1C3.4 21.4 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.4 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4V6.5h-4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l4-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.5l4 3.1c.9-2.8 3.5-4.8 6.6-4.8z" />
  </svg>
);

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.googleError || "");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden bg-lasso-ink lg:block">
        <img src={IMAGES.hero} alt="Lasso community street" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-lasso-ink via-lasso-ink/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" data-testid="login-brand-link"><Logo size="md" inverted /></Link>
          <div className="max-w-md text-white">
            <TripleSlash />
            <p className="mt-6 font-display text-3xl font-bold leading-tight">Every community. One playbook.</p>
            <p className="mt-4 text-white/70">The SOPs, training and standards that make a Lasso community feel like home — for every team member, on every site.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="lg:hidden"><Logo size="sm" /></Link>
          <h1 className="mt-10 font-display text-3xl font-bold tracking-tight lg:mt-0" data-testid="login-title">Sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground">Welcome back. Use your Lasso work account.</p>

          {error && (
            <div className="mt-6 flex gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="login-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={google}
            type="button"
            data-testid="login-google-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold transition-colors duration-200 hover:border-lasso-ink hover:bg-lasso-paper"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-7 flex items-center gap-4 text-[11px] uppercase tracking-[0.24em] text-zinc-400">
            <span className="h-px flex-1 bg-lasso-line" /> or email <span className="h-px flex-1 bg-lasso-line" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@lassocommunities.com" className="h-11" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-lasso-navy disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></>}
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            New to Lasso Hub?{" "}
            <Link to="/request-access" className="font-semibold text-lasso-ink underline decoration-lasso-sky decoration-2 underline-offset-4" data-testid="login-request-access-link">
              Request access
            </Link>
          </p>

          <div className="mt-10 border-t border-lasso-line pt-5">
            <button type="button" onClick={() => setShowDemo((s) => !s)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 hover:text-lasso-ink" data-testid="login-demo-toggle">
              Demo accounts <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDemo ? "rotate-180" : ""}`} />
            </button>
            {showDemo && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {DEMO.map((d) => (
                  <button
                    key={d.email}
                    type="button"
                    onClick={() => { setEmail(d.email); setPassword(d.password); }}
                    data-testid={`login-demo-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-lg border border-lasso-line px-3 py-2 text-left text-xs font-medium transition-colors hover:border-lasso-sky hover:bg-lasso-tint"
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
